import React from 'react';
import { Book, Music, Heart, Sparkles } from 'lucide-react';

const Program = () => {
  return (
    <section id="program" className="relative py-24 bg-gradient-to-b from-[#0a0a0a] to-[#1a1a1a]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full px-6 py-2 mb-6">
            <Sparkles className="w-5 h-5 text-[#D4AF37]" />
            <span className="text-[#D4AF37] font-bold text-sm">THE SOLUTION</span>
          </div>
          <h2 className="text-5xl sm:text-6xl md:text-7xl font-bold text-white mb-6" style={{ fontFamily: '"Bebas Neue", sans-serif' }}>
            HOW
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#D4AF37] to-[#FFD700]">
              MUSIC & FAITH TRANSFORM LIVES
            </span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            40 Songs and a Mic combines the power of gospel and hip-hop music with structured self-help workbooks to create lasting change.
          </p>
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          {/* Music Side */}
          <div className="bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] border border-[#D4AF37]/20 rounded-2xl p-8 md:p-10 transform hover:scale-105 transition-all duration-300">
            <div className="w-16 h-16 bg-gradient-to-br from-[#D4AF37] to-[#FFD700] rounded-xl flex items-center justify-center mb-6">
              <Music className="w-9 h-9 text-[#0a0a0a]" />
            </div>
            <h3 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: '"Bebas Neue", sans-serif' }}>
              MUSIC THAT HEALS
            </h3>
            <p className="text-gray-300 mb-6 leading-relaxed">
              Gospel and hip-hop aren't just genres. They're vessels of truth, redemption, and spiritual awakening. Through rhythm and lyrics, we speak directly to the hearts of those seeking transformation.
            </p>
            <ul className="space-y-3">
              <li className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-[#D4AF37] rounded-full mt-2" />
                <span className="text-gray-400">Spiritually uplifting gospel messages</span>
              </li>
              <li className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-[#D4AF37] rounded-full mt-2" />
                <span className="text-gray-400">Authentic hip-hop storytelling</span>
              </li>
              <li className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-[#D4AF37] rounded-full mt-2" />
                <span className="text-gray-400">Songs of hope, resilience, and faith</span>
              </li>
            </ul>
          </div>

          {/* Workbooks Side */}
          <div className="bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] border border-[#D4AF37]/20 rounded-2xl p-8 md:p-10 transform hover:scale-105 transition-all duration-300">
            <div className="w-16 h-16 bg-gradient-to-br from-[#D4AF37] to-[#FFD700] rounded-xl flex items-center justify-center mb-6">
              <Book className="w-9 h-9 text-[#0a0a0a]" />
            </div>
            <h3 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: '"Bebas Neue", sans-serif' }}>
              WORKBOOKS THAT BUILD
            </h3>
            <p className="text-gray-300 mb-6 leading-relaxed">
              Our carefully crafted self-help workbooks provide structured guidance for personal growth, emotional healing, and spiritual development.
            </p>
            <ul className="space-y-3">
              <li className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-[#D4AF37] rounded-full mt-2" />
                <span className="text-gray-400">3 comprehensive workbooks included</span>
              </li>
              <li className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-[#D4AF37] rounded-full mt-2" />
                <span className="text-gray-400">Proven rehabilitation frameworks</span>
              </li>
              <li className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-[#D4AF37] rounded-full mt-2" />
                <span className="text-gray-400">Practical exercises for transformation</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Impact Story */}
        <div className="bg-gradient-to-r from-[#E63946]/10 via-[#D4AF37]/5 to-[#E63946]/10 border border-[#D4AF37]/20 rounded-2xl p-8 md:p-12">
          <div className="flex items-start space-x-6">
            <div className="hidden md:block">
              <div className="w-20 h-20 bg-gradient-to-br from-[#D4AF37] to-[#FFD700] rounded-full flex items-center justify-center">
                <Heart className="w-10 h-10 text-[#0a0a0a]" />
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: '"Bebas Neue", sans-serif' }}>
                THE TRANSFORMATION HAPPENS
              </h3>
              <p className="text-xl text-gray-300 mb-4 leading-relaxed">
                When music speaks to the soul and structured learning guides the mind, real change becomes possible. Our program participants report:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-6">
                <div className="text-center">
                  <div className="text-4xl font-bold text-[#D4AF37] mb-1" style={{ fontFamily: '"Bebas Neue", sans-serif' }}>85%</div>
                  <div className="text-sm text-gray-400">Improved Mindset</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-[#D4AF37] mb-1" style={{ fontFamily: '"Bebas Neue", sans-serif' }}>92%</div>
                  <div className="text-sm text-gray-400">Spiritual Growth</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-[#D4AF37] mb-1" style={{ fontFamily: '"Bebas Neue", sans-serif' }}>78%</div>
                  <div className="text-sm text-gray-400">Hope Restored</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Program;
